// components/CardCoverImageModal.js - Cover Image Selector for Cards (GIF, YouTube, Templates)
import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiImage, FiYoutube, FiGrid, FiUpload } from 'react-icons/fi';
import { TEMPLATE_CATEGORIES, getTemplatesByCategory } from '../constants/cardTemplates';
import '../styles/CardCoverImageModal.scss';

const CardCoverImageModal = ({ visible, onClose, onSelectImage }) => {
  const [activeTab, setActiveTab] = useState('upload'); // upload, youtube, templates
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [loadingYoutube, setLoadingYoutube] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (JPG, PNG, GIF)');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size should be less than 10MB');
      return;
    }

    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      const isGif = file.type === 'image/gif';

      onSelectImage({
        uri: reader.result,
        type: isGif ? 'gif' : 'image',
        isAnimated: isGif,
        file: file,
      });
    };
    reader.readAsDataURL(file);
  };

  const extractYoutubeVideoId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const handleYoutubeSubmit = async () => {
    if (!youtubeUrl.trim()) {
      alert('Please enter a YouTube URL');
      return;
    }

    const videoId = extractYoutubeVideoId(youtubeUrl);
    if (!videoId) {
      alert('Invalid YouTube URL. Please enter a valid YouTube video URL.');
      return;
    }

    setLoadingYoutube(true);

    try {
      // Use YouTube thumbnail
      const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

      onSelectImage({
        type: 'youtube',
        videoId: videoId,
        originalUrl: youtubeUrl,
        thumbnailUri: thumbnailUrl,
        uri: thumbnailUrl,
      });
    } catch (error) {
      console.error('Error loading YouTube video:', error);
      alert('Failed to load YouTube video');
    } finally {
      setLoadingYoutube(false);
    }
  };

  const handleTemplateSelect = (template) => {
    onSelectImage({
      type: 'template',
      uri: template.preview,
      templateId: template.id,
      themeColor: template.themeColor,
      envelopeStyle: template.envelopeStyle,
      fontFamily: template.fontFamily,
      defaultTitle: template.defaultTitle,
      defaultNotes: template.defaultNotes,
    });
  };

  const filteredTemplates = getTemplatesByCategory(selectedCategory);

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="card-cover-image-modal"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="modal-header">
            <h3>Select Cover Image</h3>
            <button className="btn-close" onClick={onClose}>
              <FiX />
            </button>
          </div>

          {/* Tabs */}
          <div className="modal-tabs">
            <button
              className={`tab ${activeTab === 'upload' ? 'active' : ''}`}
              onClick={() => setActiveTab('upload')}
            >
              <FiUpload />
              <span>Upload</span>
            </button>
            <button
              className={`tab ${activeTab === 'youtube' ? 'active' : ''}`}
              onClick={() => setActiveTab('youtube')}
            >
              <FiYoutube />
              <span>YouTube</span>
            </button>
            <button
              className={`tab ${activeTab === 'templates' ? 'active' : ''}`}
              onClick={() => setActiveTab('templates')}
            >
              <FiGrid />
              <span>Templates</span>
            </button>
          </div>

          {/* Content */}
          <div className="modal-content">
            {/* Upload Tab */}
            {activeTab === 'upload' && (
              <div className="upload-section">
                <button
                  className="upload-button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FiImage className="upload-icon" />
                  <h4>Upload Image or GIF</h4>
                  <p>JPG, PNG, or GIF (max 10MB)</p>
                  <p className="upload-hint">GIFs will be animated on the card</p>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>
            )}

            {/* YouTube Tab */}
            {activeTab === 'youtube' && (
              <div className="youtube-section">
                <div className="youtube-info">
                  <FiYoutube className="youtube-icon" />
                  <h4>Add YouTube Video</h4>
                  <p>The video thumbnail will be used as the card cover</p>
                </div>
                <div className="youtube-input-group">
                  <input
                    type="text"
                    placeholder="Paste YouTube URL here..."
                    value={youtubeUrl}
                    onChange={e => setYoutubeUrl(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && handleYoutubeSubmit()}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={handleYoutubeSubmit}
                    disabled={loadingYoutube || !youtubeUrl.trim()}
                  >
                    {loadingYoutube ? 'Loading...' : 'Add Video'}
                  </button>
                </div>
              </div>
            )}

            {/* Templates Tab */}
            {activeTab === 'templates' && (
              <div className="templates-section">
                {/* Categories */}
                <div className="template-categories">
                  {TEMPLATE_CATEGORIES.map(cat => (
                    <button
                      key={cat.id}
                      className={`category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(cat.id)}
                    >
                      <span className="category-icon">{cat.icon}</span>
                      <span className="category-name">{cat.name}</span>
                    </button>
                  ))}
                </div>

                {/* Templates Grid */}
                <div className="templates-grid">
                  {filteredTemplates.map(template => (
                    <button
                      key={template.id}
                      className="template-card"
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <img src={template.preview} alt={template.name} />
                      <div className="template-overlay">
                        <div className="template-name">{template.name}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CardCoverImageModal;
